import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';

import { useTranslations } from '@lang';
import rewriteHttp from '@utils/rewriteHttp';
import { parseTime, parseTimeslotsNew } from '@utils/helpers';
import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { MappedRestaurantsType } from '@features/tables/schemas/MappedRestaurantResponse.schema';
import { PackageDiningTemplate } from 'voyager';
import type { PackageDiningDateGroup, PreferenceCategory } from 'voyager';

import { useCurrentStep, usePackageConfiguration, useWizardNavigation } from '../../contexts';
import { useNextStepLabel } from '../hooks/useNextStepLabel';
import { filterTimeslotsByExperiences } from '../../helpers/filterTimeslotsByExperiences';
import {
  getDiningDate,
  getDiningDayNumbers,
  getLastDayNumber,
  groupDiningOptionsByDate,
} from '../../helpers/groupDiningOptionsByDate';
import {
  filterTimeslotsByMenu,
  getDiningMenuOptions,
  getLeaveTimeForSelection,
  isMenuRequired,
  resolveMenuSelection,
} from '../../helpers/packageDiningMenus';
import { hasRestaurantChoice } from '../../helpers/diningRestaurantChoices';
import { useGuestExperiences } from '../hooks/useGuestExperiences';
import { DiningOption, PackagesFormValues, RestaurantBooking, TablesSchema } from '../../types';
import { usePackageDiningAvailability } from '../hooks/usePackageDiningAvailability';

const NOTES_CATEGORY = 'notes';
const DIETARY_CATEGORY = 'dietary';
const OCCASION_CATEGORY = 'occasion';

const toRestaurantValue = (restaurant: MappedRestaurantsType[number]) => ({
  restaurantName: restaurant.restaurantName,
  configName: restaurant.config.name,
  id: restaurant.config.id,
});

export const TablesStepV2 = () => {
  const { t } = useTranslations();
  const nextLabel = useNextStepLabel('common.next');
  const router = useRouter();
  const { property, language, journeyParams, rate, room } = usePackageConfiguration();
  const { currentFormStep, allFormSteps, handleNextStep, handlePreviousStep, currentStepIndex } =
    useWizardNavigation();
  const { packageTotalPrice, isSubmitting, itinerary } = useCurrentStep();
  const { watch, setValue, getValues } = useFormContext<PackagesFormValues>();

  const diningOptions = useMemo(
    () => currentFormStep.diningOptions || [],
    [currentFormStep.diningOptions]
  );

  const tables = watch('tables');
  const restaurantValues = tables?.restaurants;
  const selectedDayKey = diningOptions
    .map((_, optionIndex) => restaurantValues?.[optionIndex]?.dayNumber ?? '')
    .join(',');
  const lastDayNumber = getLastDayNumber(journeyParams.fromDate, journeyParams.toDate);

  const dateGroups = useMemo(
    () =>
      groupDiningOptionsByDate(
        diningOptions,
        journeyParams.fromDate,
        selectedDayKey.split(',').map((day) => (day === '' ? undefined : Number(day))),
        lastDayNumber
      ),
    [diningOptions, journeyParams.fromDate, selectedDayKey, lastDayNumber]
  );

  const selectedRestaurantKey = diningOptions
    .map((_, optionIndex) => restaurantValues?.[optionIndex]?.restaurant?.id ?? '')
    .join(',');
  const selectedRestaurantIds = useMemo(
    () => selectedRestaurantKey.split(',').map((id) => (id === '' ? undefined : Number(id))),
    [selectedRestaurantKey]
  );

  const availability = usePackageDiningAvailability(dateGroups, selectedRestaurantIds);
  const { data: restaurants } = useGetRestaurants(property?.id, language);
  const getRestaurantCandidates = useCallback(
    (option: DiningOption) =>
      (option.restaurantChoices ?? [option]).flatMap(
        ({ id }) => restaurants?.find(({ config }) => config.id === id) ?? []
      ),
    [restaurants]
  );

  const { data: tablesData } = useGetTablesPage(property?.id, language);
  const dietaryRequirements = tablesData?.dietaryRequirements || [];
  const celebrationOptions = tablesData?.celebrationOptions || [];

  const notesValue = tables?.notes ?? '';
  const selectedDietary = restaurantValues?.[0]?.dietaryRequirements || [];
  const selectedOccasions = restaurantValues?.[0]?.celebrationOptions || [];
  const guestExperiences = useGuestExperiences();

  const [expanded, setExpanded] = useState({
    [NOTES_CATEGORY]: !!notesValue,
    [DIETARY_CATEGORY]: selectedDietary.length > 0,
    [OCCASION_CATEGORY]: selectedOccasions.length > 0,
  });

  useEffect(() => {
    dateGroups.forEach(({ date, options }) =>
      options.forEach(({ option, optionIndex, dayNumber }) => {
        const path = `tables.restaurants.${optionIndex}` as const;
        if (getValues(`${path}.restaurant`)) {
          return;
        }

        const candidates = getRestaurantCandidates(option);
        const isRestaurantKnown = candidates.length === 1;
        const isWaitingForGuestChoice =
          hasRestaurantChoice(option) && !getValues(`${path}.visitDate`);
        if (!isRestaurantKnown && !isWaitingForGuestChoice) {
          return;
        }

        if (isRestaurantKnown) {
          setValue(`${path}.restaurant`, toRestaurantValue(candidates[0]));
        }
        setValue(`${path}.adults`, journeyParams.adults);
        setValue(`${path}.children`, journeyParams.children ?? 0);
        setValue(`${path}.dayNumber`, dayNumber);
        setValue(`${path}.visitDate`, date);
      })
    );
  }, [
    dateGroups,
    getRestaurantCandidates,
    getValues,
    setValue,
    journeyParams.adults,
    journeyParams.children,
  ]);

  useEffect(() => {
    dateGroups.forEach(({ options }) =>
      options.forEach(({ optionIndex }) => {
        const entry = availability[optionIndex];
        if (!entry?.restaurantChoice || entry.isFetching) {
          return;
        }

        const { menuIds } = entry.restaurantChoice;
        const menuOptions = getDiningMenuOptions(entry.promotions, menuIds);
        const path = `tables.restaurants.${optionIndex}.promotion` as const;

        if (menuIds.length !== 1 || menuOptions.length !== 1 || getValues(path)?.id) {
          return;
        }

        setValue(path, {
          id: menuOptions[0].value,
          name: menuOptions[0].label,
          timeslotReservationId: null,
        });
      })
    );
  }, [dateGroups, availability, getValues, setValue]);

  const buildTimingNote = (
    timeSlot: RestaurantBooking['timeslot'] | undefined,
    menuId?: string
  ) => {
    const leaveTime = getLeaveTimeForSelection(timeSlot, menuId);

    return leaveTime ? t('tables.returnInfo', { time: parseTime(leaveTime) }) : undefined;
  };

  const optionViews = dateGroups.map(({ date, options }) => ({
    date,
    options: options.map(({ option, optionIndex, dayNumber }) => {
      const { restaurant, restaurantChoice, promotions, timeSlots, isFetching } = availability[
        optionIndex
      ] || {
        promotions: [],
        timeSlots: [],
        isFetching: true,
      };

      const menuIds = restaurantChoice?.menuIds ?? [];
      const menuOptions = getDiningMenuOptions(promotions, menuIds);
      const selectedMenu = restaurantValues?.[optionIndex]?.promotion?.id ?? undefined;
      const restaurantLabel = restaurant?.label ?? '';
      const selectedTimeslot = restaurantValues?.[optionIndex]?.timeslot;
      const hasMenuRestriction = menuIds.length === 1;
      const restaurantCandidates = getRestaurantCandidates(option);
      const selectedRestaurantId = restaurantValues?.[optionIndex]?.restaurant?.id;

      const availableTimeSlots = filterTimeslotsByExperiences(
        filterTimeslotsByMenu(timeSlots, selectedMenu),
        {
          experiences: guestExperiences,
          visitDate: restaurantValues?.[optionIndex]?.visitDate ?? date,
          dayNumber,
          delayAfter: option.delayAfter,
          menuId: selectedMenu,
        }
      );

      return {
        option,
        optionIndex,
        date,
        dayOptions: getDiningDayNumbers(option, lastDayNumber).map((day) => {
          const dayDate = getDiningDate(journeyParams.fromDate, day);
          return { value: dayDate, label: moment(dayDate).locale(language).format('dddd, D MMMM') };
        }),
        restaurantCandidates,
        restaurantOptions:
          restaurantCandidates.length > 1
            ? restaurantCandidates.map(({ config, label }) => ({
                value: String(config.id),
                label,
              }))
            : undefined,
        selectedRestaurant:
          selectedRestaurantId === undefined ? undefined : String(selectedRestaurantId),
        menuOptions,
        selectedMenu,
        availableTimeSlots,
        hasMenuRestriction,
        title: hasRestaurantChoice(option)
          ? option.name || restaurantLabel
          : option.name
          ? `${option.name} - ${restaurantLabel}`
          : restaurantLabel,
        menuRequired: isMenuRequired(selectedTimeslot, menuOptions, hasMenuRestriction),
        selectedTimeslot: selectedTimeslot?.timeSlot,
        noteText: buildTimingNote(selectedTimeslot, selectedMenu),
        isLoading: isFetching,
      };
    }),
  }));

  const findOptionView = (optionId: string) =>
    optionViews
      .flatMap(({ options }) => options)
      .find(({ optionIndex }) => `${optionIndex}` === optionId);

  const handleDayChange = (optionId: string, value: string) => {
    const view = findOptionView(optionId);
    if (!view || value === view.date) {
      return;
    }

    const path = `tables.restaurants.${view.optionIndex}` as const;
    setValue(
      `${path}.dayNumber`,
      moment(value).diff(moment(journeyParams.fromDate).startOf('day'), 'days')
    );
    setValue(`${path}.visitDate`, value);
    setValue(`${path}.promotion`, undefined as never);
    setValue(`${path}.timeslot`, undefined as never, { shouldValidate: true });
  };

  const handleRestaurantChange = (optionId: string, value: string) => {
    const view = findOptionView(optionId);
    const restaurant = view?.restaurantCandidates.find(({ config }) => String(config.id) === value);
    if (!view || !restaurant) {
      return;
    }

    const path = `tables.restaurants.${view.optionIndex}` as const;
    setValue(`${path}.restaurant`, toRestaurantValue(restaurant));
    setValue(`${path}.promotion`, undefined as never);
    setValue(`${path}.timeslot`, undefined as never, { shouldValidate: true });
  };

  const handleMenuChange = (optionId: string, value: string) => {
    const view = findOptionView(optionId);
    if (!view) {
      return;
    }

    const path = `tables.restaurants.${view.optionIndex}` as const;
    setValue(`${path}.promotion`, {
      id: value,
      name: view.menuOptions.find(({ value: menuId }) => menuId === value)?.label ?? null,
      timeslotReservationId: null,
    });
    setValue(`${path}.timeslot`, undefined as never, { shouldValidate: true });
  };

  const handleTimeslotChange = (optionId: string, value: string) => {
    const view = findOptionView(optionId);
    if (!view) {
      return;
    }

    const timeSlot = view.availableTimeSlots.find(({ timeSlot: slot }) => slot === value);
    if (!timeSlot) {
      return;
    }

    const path = `tables.restaurants.${view.optionIndex}` as const;
    setValue(`${path}.timeslot`, timeSlot as never);
    setValue(
      `${path}.promotion`,
      resolveMenuSelection(
        timeSlot,
        view.selectedMenu,
        view.menuOptions,
        view.hasMenuRestriction
      ) as never,
      { shouldValidate: true }
    );
  };

  const applyToEveryOption = (
    field: 'dietaryRequirements' | 'celebrationOptions',
    flag: 'hasDietaryRequirements' | 'hasCelebrationOptions',
    values: string[]
  ) =>
    diningOptions.forEach((_, optionIndex) => {
      setValue(`tables.restaurants.${optionIndex}.${field}`, values);
      setValue(`tables.restaurants.${optionIndex}.${flag}`, values.length > 0);
    });

  const preferences: PreferenceCategory[] = [
    {
      id: NOTES_CATEGORY,
      label: t('room.guestDetails.notesTitle'),
      isFreeText: true,
      freeTextPlaceholder: t('room.guestDetails.notesPlaceholder'),
      freeTextValue: notesValue,
      collapsible: true,
      expanded: expanded[NOTES_CATEGORY],
    },
    ...(dietaryRequirements.length
      ? [
          {
            id: DIETARY_CATEGORY,
            label: t('package.dining.dietaryTitle'),
            options: dietaryRequirements,
            selectedOptions: selectedDietary,
            collapsible: true,
            allowOther: false,
            expanded: expanded[DIETARY_CATEGORY],
          },
        ]
      : []),
    ...(celebrationOptions.length
      ? [
          {
            id: OCCASION_CATEGORY,
            label: t('package.dining.occasionTitle'),
            options: celebrationOptions,
            selectedOptions: selectedOccasions,
            collapsible: true,
            allowOther: false,
            expanded: expanded[OCCASION_CATEGORY],
          },
        ]
      : []),
  ];

  const handlePreferenceExpandedChange = (categoryId: string, isExpanded: boolean) => {
    setExpanded((previous) => ({ ...previous, [categoryId]: isExpanded }));

    if (isExpanded) {
      return;
    }

    if (categoryId === NOTES_CATEGORY) {
      setValue('tables.notes', '');
    }
    if (categoryId === DIETARY_CATEGORY) {
      applyToEveryOption('dietaryRequirements', 'hasDietaryRequirements', []);
    }
    if (categoryId === OCCASION_CATEGORY) {
      applyToEveryOption('celebrationOptions', 'hasCelebrationOptions', []);
    }
  };

  const handlePreferenceOptionToggle = (categoryId: string, value: string) => {
    const current = categoryId === DIETARY_CATEGORY ? selectedDietary : selectedOccasions;
    const next = current.includes(value)
      ? current.filter((selected) => selected !== value)
      : [...current, value];

    if (categoryId === DIETARY_CATEGORY) {
      applyToEveryOption('dietaryRequirements', 'hasDietaryRequirements', next);
    } else {
      applyToEveryOption('celebrationOptions', 'hasCelebrationOptions', next);
    }
  };

  const templateDateGroups: PackageDiningDateGroup[] = optionViews.map(({ date, options }) => ({
    id: date,
    date: `${moment(date).locale(language).format('dddd, D MMMM')}.`,
    options: options.map((view) => ({
      id: `${view.optionIndex}`,
      title: view.title,
      dayOptions: view.dayOptions,
      selectedDay: view.date,
      restaurantOptions: view.restaurantOptions,
      selectedRestaurant: view.selectedRestaurant,
      menuOptions: view.menuOptions,
      selectedMenu: view.selectedMenu,
      menuRequired: view.menuRequired,
      timeslotOptions: parseTimeslotsNew(view.availableTimeSlots, view.date),
      selectedTimeslot: view.selectedTimeslot,
      noteText: view.noteText,
      isLoading: view.isLoading,
    })),
  }));

  const isValid = TablesSchema.safeParse(tables).success;
  const selectedRoom = rate.rooms?.find(({ id }) => String(id) === String(room.id));
  const imageSource = rate.imageUrl || rate.images?.[0] || selectedRoom?.imageUrl || undefined;

  const labels = useMemo(
    () => ({
      title: t('package.dining.title'),
      titleAccent: t('package.dining.subtitle'),
      dateSubtitle: t('package.dining.dateQuestion'),
      backAriaLabel: t('common.back'),
      progressLabel: t('aria.bookingProgress'),
      progressValueText: t('aria.bookingStep', {
        current: currentStepIndex + 1,
        total: allFormSteps.length,
      }),
      itineraryLabel: itinerary?.label,
      dayLabel: t('package.experiences.selectDay'),
      restaurantLabel: t('tables.restaurantSelect'),
      menuLabel: t('package.dining.menuLabel'),
      timeLabel: t('package.dining.timeLabel'),
      noTimeslots: t('tables.noTimeslots'),
      totalLabel: t('basket.total'),
      nextLabel,
    }),
    [t, nextLabel, currentStepIndex, allFormSteps.length, itinerary?.label]
  );

  return (
    <PackageDiningTemplate
      imageUrl={imageSource ? rewriteHttp(imageSource) : undefined}
      imageAlt={rate.title}
      dateGroups={templateDateGroups}
      onDayChange={handleDayChange}
      onRestaurantChange={handleRestaurantChange}
      onMenuChange={handleMenuChange}
      onTimeslotChange={handleTimeslotChange}
      preferences={preferences}
      onPreferenceExpandedChange={handlePreferenceExpandedChange}
      onPreferenceOptionToggle={handlePreferenceOptionToggle}
      onPreferenceFreeTextChange={(_, value) => setValue('tables.notes', value)}
      onPreferenceOtherChange={() => {}}
      onBack={() => (currentStepIndex > 0 ? handlePreviousStep() : router.back())}
      onItineraryClick={itinerary?.onOpen}
      currentStep={currentStepIndex + 1}
      totalSteps={allFormSteps.length}
      footerTitle={rate.title}
      totalAmount={packageTotalPrice.amount}
      currencyCode={packageTotalPrice.currencyCode}
      locale={language}
      onNext={handleNextStep}
      nextDisabled={!isValid || isSubmitting}
      isLoading={isSubmitting}
      labels={labels}
    />
  );
};
